import {
  collection, doc, increment, onSnapshot, serverTimestamp, setDoc,
} from "firebase/firestore";
import { posicaoValida, type Anuncio, type PosicaoId } from "@nhomi/core";
import { db } from "./ambiente";

/* Os anúncios que existem. Quem decide qual aparece onde é o core, com a
   mesma função que a prévia do painel usa — senão o que você vê ao
   configurar não é o que vai ao ar. */

const texto = (v: unknown): string => (typeof v === "string" ? v : "");

/** Lê o documento com desconfiança: campo faltando não pode virar `undefined`
    dentro do JSX, e posição desconhecida não pode abrir uma vaga nova. */
function paraAnuncio(id: string, d: Record<string, unknown>): Anuncio {
  const posicoes = Array.isArray(d["posicoes"]) ? d["posicoes"] : [];
  return {
    id,
    titulo: texto(d["titulo"]),
    texto: texto(d["texto"]),
    imagemPath: typeof d["imagemPath"] === "string" && d["imagemPath"]
      ? d["imagemPath"] : null,
    alt: texto(d["alt"]),
    url: texto(d["url"]),
    cta: texto(d["cta"]),
    posicoes: posicoes.filter(posicaoValida) as PosicaoId[],
    ativo: d["ativo"] === true,
    de: texto(d["de"]),
    ate: texto(d["ate"]),
  };
}

export function watchAnuncios(cb: (lista: Anuncio[]) => void): () => void {
  return onSnapshot(
    collection(db, "ads"),
    (snap) => cb(snap.docs.map((d) => paraAnuncio(d.id, d.data()))),
    () => cb([]),
  );
}

/* ---------- medir ---------- */

/**
 * O dia como o servidor conta, em São Paulo.
 *
 * Mesmo motivo do contador de estimativas: o agregado é somado por um
 * gatilho, e duas metades da mesma conta em fusos diferentes fariam a
 * virada do dia contar errado para quem está viajando.
 */
const diaDoServidor = (): string =>
  new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/Sao_Paulo",
    year: "numeric", month: "2-digit", day: "2-digit",
  }).format(new Date());

const meuDoc = (uid: string, adId: string) =>
  doc(db, "adStats", adId, "dias", diaDoServidor(), "vistos", uid);

/**
 * Um anúncio visto duas vezes em segundos é a mesma impressão.
 *
 * Rolar o feed pra cima e pra baixo remonta o componente. Sem esta
 * janela, a mesma pessoa parada na mesma tela inflaria a contagem — e o
 * número que o anunciante recebe deixaria de significar alguma coisa.
 */
const JANELA = 30_000;
const vistosAgora = new Map<string, number>();

export async function registrarImpressao(uid: string, adId: string): Promise<void> {
  const agora = Date.now();
  const ultimo = vistosAgora.get(adId) ?? 0;
  if (agora - ultimo < JANELA) return;
  vistosAgora.set(adId, agora);

  await setDoc(meuDoc(uid, adId), {
    impressoes: increment(1), at: serverTimestamp(),
  }, { merge: true }).catch((err) => {
    // Medição não pode derrubar o app: falhar aqui só perde um número.
    vistosAgora.delete(adId);
    reclamar("impressão", err);
  });
}

export async function registrarClique(uid: string, adId: string): Promise<void> {
  await setDoc(meuDoc(uid, adId), {
    cliques: increment(1), at: serverTimestamp(),
  }, { merge: true }).catch((err) => reclamar("clique", err));
}

/**
 * Engolir o erro não pode significar escondê-lo.
 *
 * A primeira versão daqui tinha `.catch(() => {})` puro, e a regra do
 * Firestore recusava TODA impressão. Nada aparecia: nem na tela, que não
 * deve mesmo mostrar, nem no console, onde alguém teria visto em cinco
 * segundos. O painel mostrava zero com o anúncio rodando, e a única
 * saída foi reproduzir no emulador.
 *
 * Continuar engolindo está certo — anúncio quebrado não pode quebrar o
 * feed. Fazer isso em silêncio, não.
 */
function reclamar(o_que: string, err: unknown): void {
  console.warn(`[anúncio] não deu pra contar ${o_que}:`, err);
}
